import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import "react-toastify/dist/ReactToastify.css";
import { registerLocale } from "react-datepicker";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import {
  COLOR_CONTENT_AI_APPROVAL_STATUS,
  COLOR_CONTENT_CREATED_TYPE,
  COLOR_CONTENT_STATUS,
  ENUM_CONTENT_AI_APPROVAL_STATUS,
  ENUM_CONTENT_CREATED_TYPE,
  ENUM_CONTENT_TYPE,
  LIST_CONTENT_AI_APPROVAL_STATUS_OPTION,
  LIST_CONTENT_CREATED_TYPE_OPTION,
  LIST_CONTENT_SOURCE_OPTION,
  LIST_CONTENT_STATUS_OPTION,
} from "helpers/constans";
import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import { toast } from "react-toastify";
import NO_IMAGE_USER from "../../../assets/images/no-user.png";
import UploadFileBK from "components/Common/UploadFileBK";
import UploadImage from "components/Common/UploadImage";
import RatingDefinitionContent from "components/Common/RatingDefinitionContent";
import { createContent, updateContent } from "store/content/thunk";
import contentApi from "api/contentApi";
import DropdownAsyncJobSeekers from "components/Common/DropdownAsyncJobSeekers";
import DatePicker from "react-datepicker";
import { formatDateStringToKorea, formatDateStringToUTC } from "helpers/format";

export interface Props {
  id?: string;
  item?: any;
  triggerRefresh?: () => void;
  isShowDetail?: boolean;
  onCloseClick?: any;
  dataRatingDefinition?: any;
  dataContentType?: any;
  setLinkImage?: any;
}

registerLocale("en", en);
registerLocale("ko", ko);

const DEFAULT_CONTENT_TYPE_REVIEW = 2;
const ContentForm = ({
  id = "",
  item,
  dataRatingDefinition = [],
  dataContentType = [],
  triggerRefresh,
  isShowDetail = false,
  onCloseClick,
  setLinkImage,
}: Props) => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userSelected, setUserSelected] = useState<any>(null);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);

      let data: any = {
        postTypeId: values?.postTypeId,
        title: values?.title,
        contents: values?.contents,
        files: values?.files,
        createdType: values?.createdType,
        nickname: values?.nickname,
        avatar: values?.avatar?.length > 0 ? values?.avatar?.[0]?.url : "",
        status: values?.status,
        ...(values?.createdType === ENUM_CONTENT_CREATED_TYPE.NORMAL ? {userId: values?.userId} : null),
        generatedByAI: values?.generatedByAI,
        AIApprovalStatus: values?.AIApprovalStatus,
        publishedAt: formatDateStringToUTC(values?.publishedAt || new Date()),
      }

      if(values?.postTypeId === DEFAULT_CONTENT_TYPE_REVIEW) {
        data = {
          ...data,
          storeName: values?.storeName,
          ratings: values?.ratings
        };
      }

      const response: any = id
        ? await updateContent(id, data)
        : await createContent(data);

      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${id ? t("The content has been updated.") : t("The content has been created.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    postTypeId: Yup.number().required(`${t("This field is required")}`),
    createdType: Yup.string().required(`${t("This field is required")}`),
    userId: Yup.string().when("createdType", {
      is: ENUM_CONTENT_CREATED_TYPE.NORMAL,
      then: (schema) => schema.required(t("This field is required")),
    }),
    nickname: Yup.string().when("createdType", {
      is: ENUM_CONTENT_CREATED_TYPE.SYSTEM,
      then: (schema) => schema.required(t("This field is required")),
    }).nullable(),
    storeName: Yup.string().when("type", {
      is: DEFAULT_CONTENT_TYPE_REVIEW,
      then: (schema) => schema.required(t("This field is required")),
    }).nullable(),
    title: Yup.string().required(`${t("This field is required")}`),
    contents: Yup.string().required(`${t("This field is required")}`),
    status: Yup.string().required(`${t("This field is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      postTypeId: 1,
      generatedByAI: false,
      AIApprovalStatus: ENUM_CONTENT_AI_APPROVAL_STATUS.PENDING,
      createdType: "normal",
      storeName: "",
      nickname: "",
      avatar: "",
      userId: "",
      title: "",
      contents: "",
      files: null,
      ratings: null,
      status: "ACTIVE",
      publishedAt: new Date() || "",
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleSetValueForm = (value: any) => {
    if (value?.userId) {
      const user = value?.user;
      setUserSelected({
        value: String(value?.userId),
        label: (
          <div className="d-flex align-items-center gap-2">
            <div
              className="overflow-hidden object-fit-cover"
              style={{
                height: "22px",
                width: "22px",
                minWidth: "22px",
                borderRadius: "15px",
                backgroundColor: "#eaeaea",
                // zIndex: 99,
              }}
            >
              <img
                className="overflow-hidden object-fit-cover rounded-circle"
                src={user?.profile?.imageUrls?.[0] || NO_IMAGE_USER}
                alt={user?.profile?.nickname}
                height={22}
                width={22}
                style={{
                  minWidth: "22px",
                  backgroundColor: "#eaeaea",
                }}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null; // prevents looping
                  currentTarget.src = NO_IMAGE_USER;
                }}
              />
            </div>
            <div>
              {
                user?.profile ? `${user?.profile?.nickname || user?.profile?.fullname || ""} ${user?.profile?.email ? " - " + user?.profile?.email : ""}` : 
                user?.email || "-"
              }
            </div>
          </div>
        ),
        data: user,
      });
    }

    const data = {
      ...value,
      avatar: value?.avatar ? [{url: value?.avatar}] : [],
      publishedAt: value?.publishedAt ? new Date(value?.publishedAt) : null,
    }

    formik.setValues(data);
  }

  useEffect(() => {
    if(dataRatingDefinition?.length > 0) {
      const formatRating = dataRatingDefinition?.map((item: any) => {
        return {
          key: item?.key,
          value: item?.scale || true,
          type: item?.type 
        }
      })
      formik.setFieldValue("ratings", formatRating)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataRatingDefinition]);

  useEffect(() => {
    if(id) {
      const handleContentDetail = async () => {
        try {
          const response: any = await contentApi.getContent(id);
          if(response?.data) {
            handleSetValueForm(response?.data)
          }
        }catch(error) {
          console.log(error)
        }
      }
      handleContentDetail() 
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    document.title = `${t("Create Content")} - ${t("Content Management")} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <Modal isOpen={true} centered={true} scrollable={false} size="xl">
        <ModalHeader toggle={isLoading ? () => {} : onCloseClick && onCloseClick}>
          {id ? t("Update Content") : t("Create Content")}
        </ModalHeader>
        <ModalBody className="py-3 px-4">
          <div className="mb-4">
            <Progress
              value={
                100 - (100 / 6) * Number(Object.keys(formik.errors).length)
              }
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>

          <div className="mt-3">
            <>
              <form onSubmit={formik.handleSubmit}>
                <Row>
                  {
                    id && (
                      <>
                        <Col lg={6}>
                          <div className="mb-3">
                            <Label htmlFor="titleKoreaInput" className="form-label">
                              {t("Source")}{" "}
                              <span className="text-danger">*</span>
                            </Label>

                            <div className="d-flex justify-content-start align-items-center">
                              {LIST_CONTENT_SOURCE_OPTION?.map((item, index) => {
                                return (
                                  <div
                                    className="d-flex justify-content-start align-items-center form-check me-3"
                                    key={index}
                                  >
                                    <input
                                      className="form-check-input me-2"
                                      type="radio"
                                      name="source"
                                      id={`source-${String(item?.value)}`}
                                      value={item?.value}
                                      checked={item?.value === (formik?.values?.generatedByAI === true ? 1 : 0)}
                                      style={{ minWidth: "16px" }}
                                      onChange={(event) => {
                                        formik.setFieldValue(
                                          "generatedByAI",
                                          item?.value === 1 ? true : false
                                        );
                                      }}
                                    />
                                    <label
                                      className="form-check-label position-relative"
                                      htmlFor={`source-${String(item?.value)}`}
                                    >
                                      {t(item?.label)}
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </Col>

                        {
                          formik?.values?.generatedByAI === true && (
                            <Col lg={6} xxl={6}>
                              <div className="mb-3">
                                <label className="form-label">
                                  {t("Approved Status")}{" "}
                                  <span className="text-danger"> *</span>
                                </label>

                                <DropdownStatusNew
                                  name="AIApprovalStatus"
                                  dataList={LIST_CONTENT_AI_APPROVAL_STATUS_OPTION || []}
                                  placeholder={`${t("Approved Status")}...`}
                                  className="dropdown-status-rounded"
                                  classNamePrefix="name-prefix"
                                  initialValue={formik?.values?.AIApprovalStatus}
                                  onChangeSelect={(event: any) => {
                                    formik.setFieldValue("AIApprovalStatus", event?.value);
                                  }}
                                  isHasOptionAll={false}
                                  colors={COLOR_CONTENT_AI_APPROVAL_STATUS}
                                />

                                {formik.touched.AIApprovalStatus &&
                                formik.errors.AIApprovalStatus ? (
                                  <div className="text-danger mt-2">
                                    {formik.errors.AIApprovalStatus}
                                  </div>
                                ) : null}
                              </div>
                            </Col>
                          )
                        }
                      </>
                    )
                  }

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="titleKoreaInput" className="form-label">
                        {t("Content Type")}{" "}
                        <span className="text-danger">*</span>
                      </Label>

                      <DropdownStatusNew
                        name="postTypeId"
                        dataList={dataContentType || []}
                        placeholder={`${t("Content Type")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik?.values?.postTypeId}
                        onChangeSelect={(event: any) => {
                          formik.setFieldTouched("postTypeId", true);
                          formik.setFieldValue("postTypeId", event?.value);
                        }}
                        isHasOptionAll={false}
                      />
                      {formik.touched.postTypeId &&
                        formik.errors.postTypeId ? (
                          <div className="text-danger mt-2">
                            {formik.errors.postTypeId}
                          </div>
                        ) : null}
                    </div>
                  </Col>

                  <Col xxl={6}>
                    <div className="mb-3">
                      <label className="form-label">
                        {t("Created Type")}{" "}
                        <span className="text-danger"> *</span>
                      </label>

                      <DropdownStatusNew
                        name="createdType"
                        dataList={LIST_CONTENT_CREATED_TYPE_OPTION || []}
                        placeholder={`${t("Created Type")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik?.values?.createdType}
                        onChangeSelect={(event: any) => {
                          formik.setFieldTouched("createdType", true);
                          formik.setFieldValue("createdType", event?.value);
                          if(event?.value === ENUM_CONTENT_CREATED_TYPE.SYSTEM) {
                            formik.setFieldValue("userId", "");
                            setUserSelected(null)
                          }else {
                            formik.setFieldValue("nickname", "");
                            formik.setFieldValue("avatar", "");
                          }
                        }}
                        isHasOptionAll={false}
                        colors={COLOR_CONTENT_CREATED_TYPE}
                        disabled={!!id}
                      />
                      {formik.touched.createdType &&
                      formik.errors.createdType ? (
                        <div className="text-danger mt-2">
                          {formik.errors.createdType}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  {
                    formik.values?.createdType === ENUM_CONTENT_CREATED_TYPE.SYSTEM ? (
                      <>
                        <Col lg={6}>
                          <div className="mb-3">
                            <Label htmlFor="nickname" className="form-label">
                              {t("Nick Name")} <span className="text-danger">*</span>
                            </Label>
                            <Input
                              id="nickname"
                              name="nickname"
                              type="text"
                              className="form-control"
                              placeholder={t("Nick Name")}
                              value={formik?.values?.nickname}
                              onChange={(event: any) =>
                                formik.setFieldValue("nickname", event?.target?.value)
                              }
                              disabled={!!id}
                            />
                            {formik.touched.nickname && formik.errors.nickname ? (
                              <div className="text-danger mt-2">
                                {formik.errors.nickname}
                              </div>
                            ) : null}
                          </div>
                        </Col>
                        <Col xxl={12}>
                          <div className="mb-3">
                            <label className="form-label">
                              {t("Avatar")}
                            </label>

                            <UploadImage
                              data={formik.getFieldProps("avatar")?.value}
                              setLinkImage={setLinkImage}
                              formik={formik}
                              isMultiple={false}
                              formikNameImage="avatar"
                              isDisabled={!!id}
                            />

                            {formik.touched.avatar && formik.errors.avatar ? (
                              <div className="text-danger mt-2">
                                {formik.errors.avatar}
                              </div>
                            ) : null}
                          </div>
                        </Col>
                      </>
                    ) : (
                      <Col md={6} lg={6}>
                        <div className="mb-3">
                          <Label className="form-label">{t("Users")} <span className="text-danger">*</span></Label>

                          <DropdownAsyncJobSeekers
                            name="job-seeker"
                            onChangeSelect={(event: any) => {
                              setUserSelected(event);
                              formik.setFieldTouched("userId", true);
                              formik.setFieldValue("userId", event?.value);
                            }}
                            isMulti={false}
                            placeholder={`${t("Select User")}...`}
                            listCheckedUsers={userSelected}
                            isAllOption={false}
                            defaultParams={{
                              type: "APPLICANT",
                              isReal: true
                            }}
                            key={`list-job-seeker`}
                            isDisabled={!!id}
                          />

                          {formik.touched.userId && formik.errors.userId ? (
                            <div className="text-danger mt-2">
                              {formik.errors.userId}
                            </div>
                          ) : null}
                        </div>
                      </Col>
                    )
                  }

                  {
                    formik.values?.postTypeId === ENUM_CONTENT_TYPE.REVIEW && (
                      <>
                        <Col lg={12}>
                          <div className="mb-3">
                            <Label htmlFor="storeName" className="form-limitType">
                              {t("Content Store Name")} <span className="text-danger">*</span>
                            </Label>
                            <Input
                              id="storeName"
                              name="storeName"
                              type="text"
                              className="form-control"
                              placeholder={t("Content Store Name")}
                              value={formik?.values?.storeName}
                              onChange={(event: any) =>
                                formik.setFieldValue("storeName", event?.target?.value)
                              }
                            />
                            {formik.touched.storeName && formik.errors.storeName ? (
                              <div className="text-danger mt-2">
                                {formik.errors.storeName}
                              </div>
                            ) : null}
                          </div>
                        </Col>
                        {
                          dataRatingDefinition?.length > 0 && (
                            <RatingDefinitionContent data={dataRatingDefinition} formik={formik} />
                          )
                        }
                      </>
                    )
                  }
                  
                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="title" className="form-label">
                        {t("Title")} <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="title"
                        name="title"
                        type="text"
                        className="form-control"
                        placeholder={t("Title")}
                        value={formik?.values?.title}
                        onChange={(event: any) =>
                          formik.setFieldValue("title", event?.target?.value)
                        }
                      />
                      {formik.touched.title && formik.errors.title ? (
                        <div className="text-danger mt-2">
                          {formik.errors.title}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="contents" className="form-label">
                        {t("Content")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="contents"
                        name="contents"
                        type="textarea"
                        rows="5"
                        maxLength={1000}
                        className="form-control"
                        placeholder={t("Content")}
                        value={formik?.values?.contents}
                        onChange={(event: any) =>
                          formik.setFieldValue(
                            "contents",
                            event?.target?.value
                          )
                        }
                      />
                      {formik.touched.contents &&
                      formik.errors.contents ? (
                        <div className="text-danger mt-2">
                          {formik.errors.contents}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col md={12} lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="store detail photo" className="form-label">
                        {t("Attachment")}<br />
                        <small>
                          {t("Upload up to 100MB in jpg, png image file format. Maximize 1 file")}
                        </small>
                      </Label>
                      <UploadFileBK
                        formik={formik}
                        fieldValue="files"
                        isMultiple={false}
                        isUploadMore={
                          !formik?.values?.files ||
                          !(formik?.values?.files as any)?.length
                        }
                        setLinkImage={setLinkImage}
                      />
                      {formik.touched.files && formik.errors.files ? (
                        <div className="text-danger mt-2">
                          {formik.errors.files}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                  
                  <Col lg={3}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("Status")} <span className="text-danger">*</span>
                      </Label>

                      <DropdownStatusNew
                        name="status"
                        dataList={LIST_CONTENT_STATUS_OPTION || []}
                        placeholder={`${t("Select status")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik.values.status}
                        onChangeSelect={(e: any) => {
                          formik.setFieldValue("status", e?.value);
                        }}
                        colors={COLOR_CONTENT_STATUS}
                      />
                    </div>
                  </Col>

                  <Col md={5} lg={5}>
                    <div className="mb-3">
                      <Label htmlFor="publishedAt" className="form-label">
                        {t("Published at")}
                      </Label>
                      <div className="w-100 datepicker-time">
                        <DatePicker
                          name={"preferred-contact-time"}
                          className="form-control w-100"
                          selected={formik?.values?.publishedAt}
                          placeholderText={t("Published at")}
                          onChange={(date: any) => {
                            formik.setFieldValue("publishedAt", date);
                          }}
                          dateFormat="yyyy-MM-dd HH:mm"
                          showTimeSelect
                          // isClearable={true}
                        />
                      </div>
                      {formik.touched.publishedAt && formik.errors.publishedAt ? (
                        <div className="text-danger mt-2">
                          {String(formik.errors.publishedAt)}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="hstack gap-2 justify-content-end">
                      {!isShowDetail && (
                        <button type="submit" className="btn btn-primary fs-14">
                          {isLoading ? (
                            <Spinner size="sm" className="me-2"></Spinner>
                          ) : !!id ? (
                            <i className="ri-login-circle-line align-bottom me-2 "></i>
                          ) : (
                            <i className="ri-add-fill align-bottom me-2"></i>
                          )}
                          {id ? t("Update Content") : t("Create Content")}
                        </button>
                      )}
                    </div>
                  </Col>
                </Row>
              </form>
            </>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ContentForm;
